import React, { ReactElement, useEffect } from 'react';
import { useRouter } from 'next/router';
import { Box } from '@mui/material';
import {
  useProfile,
  useIsFullWidth,
  useIsAdmin,
  useIsApprover,
  useIsOrganizationLeader,
} from 'hooks';
import { GetSchoolResponse } from 'services/api';
import { EventCheckInSheet } from 'components/modals/event-check-in';
import { TimeSlotModal } from 'components/modals/TimeSlotModal';
import { SectionReveal } from './shared';
import {
  DEFAULT_CAMPUS_RESOURCES,
  CAMPUS_RESOURCE_FIELDS,
  CampusResource,
} from './shared/resources.config';
import { ForYouShell } from './ForYouShell';
import {
  SchoolHeader,
  ActionRequiredList,
  YourOrgs,
  CampusResourcesWidget,
} from './sections';
import { FeedSectionHeader } from './sections/rail/FeedSectionHeader';
import { HappeningToday } from './sections/rail/HappeningToday';
import { UpcomingRail } from './sections/rail/UpcomingRail';
import { useConnectedFeed } from './sections/rail/useConnectedFeed';

export interface ForYouPageProps {
  slug: string | null;
  school?: GetSchoolResponse;
}

const FeedMain = ({
  desktop,
  loading,
  todayCount,
  upcomingCount,
  adminEnabled,
  happeningToday,
  upcoming,
  yourOrgs,
  actionRequired,
}: {
  desktop: boolean;
  loading: boolean;
  todayCount: number;
  upcomingCount: number;
  adminEnabled: boolean;
  happeningToday: ReactElement;
  upcoming: ReactElement;
  yourOrgs: ReactElement;
  actionRequired: ReactElement;
}): ReactElement => {
  const showToday = loading || todayCount > 0;
  const showUpcoming = loading || upcomingCount > 0;

  if (desktop) {
    return (
      <>
        {showToday && (
          <SectionReveal index={0} ariaLabel="Happening today">
            {happeningToday}
          </SectionReveal>
        )}
        {showUpcoming && (
          <SectionReveal index={1} ariaLabel="Upcoming">
            {upcoming}
          </SectionReveal>
        )}
        <SectionReveal index={2} ariaLabel="Your organizations">
          {yourOrgs}
        </SectionReveal>
      </>
    );
  }

  return (
    <>
      {showToday && (
        <SectionReveal index={1} ariaLabel="Happening today">
          {happeningToday}
        </SectionReveal>
      )}
      {adminEnabled && (
        <SectionReveal index={2} ariaLabel="Action required">
          {actionRequired}
        </SectionReveal>
      )}
      {showUpcoming && (
        <SectionReveal index={3} ariaLabel="Upcoming">
          {upcoming}
        </SectionReveal>
      )}
      <SectionReveal index={4} ariaLabel="Your organizations">
        {yourOrgs}
      </SectionReveal>
    </>
  );
};

export const ForYouPage = ({ slug, school }: ForYouPageProps): ReactElement => {
  const router = useRouter();
  const isDesktop = useIsFullWidth();
  const { profile } = useProfile();

  const isAdmin = useIsAdmin();
  const isLeader = useIsOrganizationLeader();
  const isApprover = useIsApprover();
  const adminEnabled = Boolean(isAdmin || isLeader || isApprover);

  const schoolId = profile?.school?.id;
  const schoolName = school?.name ?? profile?.school?.name;
  const bannerUrl = school?.bannerUrl;
  const iconUrl = school?.iconUrl ?? profile?.school?.iconUrl;
  const headerLoading = !profile;

  const feed = useConnectedFeed(slug);

  const goToOrganizations = (): void => {
    void router.replace(
      {
        pathname: router.pathname,
        query: { ...router.query, tab: 'organizations' },
      },
      undefined,
      { shallow: true },
    );
  };

  useEffect(() => {
    if (router.query.tab === undefined) {
      void router.replace(
        {
          pathname: router.pathname,
          query: { ...router.query, tab: 'for-you' },
        },
        undefined,
        { shallow: true },
      );
    }
  }, []);

  const schoolHeader = (
    <SchoolHeader
      name={schoolName}
      bannerUrl={bannerUrl}
      iconUrl={iconUrl}
      loading={headerLoading}
    />
  );

  const configuredResources: CampusResource[] = DEFAULT_CAMPUS_RESOURCES.map(
    resource => {
      const field = CAMPUS_RESOURCE_FIELDS.find(f => f.id === resource.id);
      const href = field ? school?.campusResourceUrls?.[field.key] : undefined;
      return { ...resource, href };
    },
  ).filter(resource => Boolean(resource.href));

  const campusResources = configuredResources.length ? (
    <CampusResourcesWidget resources={configuredResources} />
  ) : null;

  const actionRequired = (
    <ActionRequiredList
      schoolId={schoolId}
      enabled={adminEnabled}
      showEyebrow={!isDesktop}
    />
  );

  const happeningToday = (
    <HappeningToday
      events={feed.today}
      loading={feed.loading}
      desktop={isDesktop}
      hostingIds={feed.hostingIds}
      loadingId={feed.loadingId}
      onOpen={feed.openEvent}
      onCheckIn={event => {
        void feed.openCheckIn(event, 'happening_today');
      }}
    />
  );

  const upcoming = (
    <UpcomingRail
      events={feed.upcoming}
      loading={feed.loading}
      desktop={isDesktop}
      hostingIds={feed.hostingIds}
      loadingId={feed.loadingId}
      onOpen={feed.openEvent}
      onAction={event => {
        if (
          feed.hostingIds.has(event.id) ||
          event.reserved ||
          event.checkedIn
        ) {
          void feed.openCheckIn(event, 'upcoming');
          return;
        }
        void feed.rsvp(event);
      }}
      onSeeAll={feed.seeAllEvents}
    />
  );

  const yourOrgs = (
    <Box>
      <FeedSectionHeader
        title="Your organizations"
        onViewMore={goToOrganizations}
      />
      <Box mt="16px">
        <YourOrgs hideHeader compact />
      </Box>
    </Box>
  );

  const desktopSidebar = (
    <>
      <SectionReveal index={0} component="header" ariaLabel="School">
        {schoolHeader}
      </SectionReveal>
      {adminEnabled && (
        <SectionReveal
          index={1}
          ariaLabel="Action required"
          sx={{ px: '20px' }}
        >
          {actionRequired}
        </SectionReveal>
      )}
      {campusResources && (
        <SectionReveal
          index={2}
          ariaLabel="Campus resources"
          sx={{ px: '20px' }}
        >
          {campusResources}
        </SectionReveal>
      )}
    </>
  );

  const children = (
    <FeedMain
      desktop={isDesktop}
      loading={feed.loading}
      todayCount={feed.today.length}
      upcomingCount={feed.upcoming.length}
      adminEnabled={adminEnabled}
      happeningToday={happeningToday}
      upcoming={upcoming}
      yourOrgs={yourOrgs}
      actionRequired={actionRequired}
    />
  );

  return (
    <>
      <ForYouShell
        activeTab="for-you"
        documentTitle="CampusLush | For You"
        desktopSidebar={desktopSidebar}
        mobileBanner={schoolHeader}
        bleedMobileBanner
        mobileAfter={
          campusResources ? (
            <SectionReveal index={5} ariaLabel="Campus resources">
              {campusResources}
            </SectionReveal>
          ) : null
        }
      >
        {children}
      </ForYouShell>
      {feed.sheet && (
        <EventCheckInSheet
          open
          onClose={feed.closeSheet}
          event={feed.sheet.event}
          forceOrganizer={feed.sheet.organizer}
          receiptId={feed.sheet.receiptId}
          tickets={feed.sheet.tickets}
        />
      )}
      {feed.timeSlotEvent && (
        <TimeSlotModal
          open={feed.timeSlotOpen}
          onClose={feed.closeTimeSlot}
          event={feed.timeSlotEvent}
          onReservationComplete={feed.completeTimeSlot}
        />
      )}
    </>
  );
};
